import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ResetDynamicData } from '../../../../utilities/BL/Xtp';

const MASTER_PASSWORD_REQUIRED = 'evitarborrarregistros456852';

/**
 * MODAL: XtpResetModal
 * Zona de Riesgo - Reinicio Maestro y Borrado de Registros con Contraseña.
 * Protege contra borrados accidentales validando la clave maestra 'evitarborrarregistros456852'.
 */
export const XtpResetModal = ({
    isOpen = false,
    onClose = () => {},
    onResetSuccess = () => {},
    showMessage = () => {}
}) => {
    const [passwordInput, setPasswordInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (isOpen) {
            setPasswordInput('');
            setErrorMessage('');
            setIsSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = async (e) => {
        if (e) e.preventDefault();

        if (passwordInput.trim() !== MASTER_PASSWORD_REQUIRED) {
            setErrorMessage('Contraseña incorrecta. No tienes autorización para reiniciar la dinámica.');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');

        try {
            const res = await ResetDynamicData(passwordInput.trim(), 'Staff Admin');
            if (res && res.success) {
                showMessage({
                    type: 'success',
                    message: res.message || 'La dinámica ha sido reiniciada exitosamente.'
                });
                onResetSuccess();
                onClose();
            } else {
                setErrorMessage(res?.message || 'Error al ejecutar el reinicio de registros en el servidor.');
            }
        } catch (error) {
            console.error('Error en XtpResetModal:', error);
            setErrorMessage('Error de conexión con el servidor al procesar el reinicio.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1060 }}
        >
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                    {/* Header */}
                    <div className="modal-header bg-danger text-white py-3 px-4 border-0">
                        <h5 className="modal-title fw-bold d-flex align-items-center gap-2 mb-0">
                            <i className="bi bi-radioactive fs-4"></i>
                            Zona Peligrosa: Reiniciar Dinámica
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Cerrar"
                            onClick={onClose}
                            disabled={isSubmitting}
                        />
                    </div>

                    {/* Body */}
                    <div className="modal-body p-4 bg-white text-dark">
                        {errorMessage && (
                            <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" role="alert">
                                <i className="bi bi-exclamation-triangle-fill fs-5"></i>
                                <span className="small fw-semibold">{errorMessage}</span>
                            </div>
                        )}

                        <div className="p-3 bg-danger-subtle rounded-3 border border-danger border-opacity-25 mb-3">
                            <div className="d-flex align-items-start gap-2">
                                <i className="bi bi-shield-exclamation text-danger fs-3 flex-shrink-0"></i>
                                <div>
                                    <h6 className="text-danger fw-bold mb-1">¡Acción Destructiva Irreversible!</h6>
                                    <p className="small text-danger-emphasis mb-0">
                                        Esta operación eliminará de forma permanente <strong>todos los registros de tickets de los aficionados</strong>,
                                        pases emitidos, registros de canje y telemetría de la dinámica. Los contadores volverán a 0.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleConfirm}>
                            <div className="mb-3">
                                <label className="form-label fw-bold small text-secondary">
                                    Para confirmar, ingresa la contraseña maestra de seguridad:
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light text-muted border-end-0">
                                        <i className="bi bi-key-fill"></i>
                                    </span>
                                    <input
                                        type="password"
                                        className="form-control border-start-0"
                                        placeholder="Ingresa la contraseña"
                                        value={passwordInput}
                                        onChange={(e) => setPasswordInput(e.target.value)}
                                        autoFocus
                                        disabled={isSubmitting}
                                        autoComplete="current-password"
                                    />
                                </div>
                                <div className="form-text text-muted small mt-1">
                                    Esta contraseña previene la eliminación accidental o no autorizada de participaciones.
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                                <button
                                    type="button"
                                    className="btn btn-secondary px-4 fw-semibold"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-danger px-4 fw-bold d-flex align-items-center gap-2"
                                    disabled={!passwordInput.trim() || isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                            <span>Borrando datos...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-trash-fill"></i>
                                            <span>Confirmar y Borrar Todo</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

XtpResetModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onResetSuccess: PropTypes.func,
    showMessage: PropTypes.func
};

export default XtpResetModal;
